"use client";

import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useIntl } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";

type HomeErrorProps = {
  /**
   * `digest` is Next's own field. In production the boundary is handed a redacted error,
   * so the digest is the only key back to the real message in the server log.
   */
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * Error boundary for the home route, reached only on a cold render — ISR serves the last
 * good page otherwise. Dark ground: the navbar is transparent here, so its links are white.
 */
const HomeError = ({ error, reset }: HomeErrorProps) => {
  const intl = useIntl();

  useEffect(() => {
    // Report explicitly — a boundary catches the error, so nothing else would. The digest
    // travels as context: without it a production report says only that the render failed.
    datadogRum.addError(error, { digest: error.digest });
  }, [error]);

  return (
    <main
      className="tw-flex tw-min-h-screen tw-flex-col tw-items-center tw-justify-center tw-gap-16 tw-bg-gray-900 tw-px-16 tw-text-center"
      data-testing-id="home-error"
    >
      <Text
        element="h1"
        typeface="headline-4"
        className="tw-text-base-white"
        data-testing-id="home-error-title"
      >
        {intl.formatMessage({
          defaultMessage: "We couldn't load this page.",
        })}
      </Text>
      <Text
        element="p"
        typeface="body-1"
        className="tw-text-base-white"
        data-testing-id="home-error-message"
      >
        {intl.formatMessage({
          defaultMessage: "Give it another try in a moment.",
        })}
      </Text>
      <Button
        appearance="white"
        data-testing-id="home-error-retry"
        onClick={reset}
      >
        {intl.formatMessage({ defaultMessage: "Try again" })}
      </Button>
    </main>
  );
};

export default HomeError;
